/**
 * Shared service-request catalog — the ONE list of orderable services used by
 * every request surface (public wizard, Lite, the AERO FBO handheld intake
 * and edit forms) and by the api-server when seeding per-service tracking
 * items. Keeping it here prevents the catalogs from drifting apart.
 *
 * Fuel is ordered as an explicit product ("Jet-A Fuel" / "100LL Avgas" /
 * "SAF Blend"), never as a generic "Fuel" chip. Jet-A additionally carries a
 * yes/no Prist additive choice which resolves the stored fuel type.
 */

/** Fuel products offered as services. SAF is gated on the FBO's flag. */
export const FUEL_SERVICE_OPTIONS = [
  "Jet-A Fuel",
  "100LL Avgas",
  "SAF Blend",
] as const;

export const SAF_SERVICE_NAME = "SAF Blend";

/** General (line) services, fuel products first. */
export const GENERAL_SERVICE_OPTIONS = [
  ...FUEL_SERVICE_OPTIONS,
  "Quick Turn",
  "GPU",
  "Lavatory Service",
  "Potable Water",
  "Hangar",
  "De-icing",
  "Aircraft Cleaning",
] as const;

/** Concierge services — tracked with passenger / provider details. */
export const CONCIERGE_SERVICE_OPTIONS = [
  "Rental Car",
  "Limousine / Car Service",
  "Ground Transport",
  "Catering",
  "Customs / FBO Handling",
] as const;

export const ALL_SERVICE_OPTIONS = [
  ...GENERAL_SERVICE_OPTIONS,
  ...CONCIERGE_SERVICE_OPTIONS,
] as const;

/** Loose key for matching service names across spelling variants. */
function serviceKey(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

/**
 * Known synonyms for catalog services. FBO listed-service arrays were free
 * text for a long time, so facilities carry entries like "Hangar Storage",
 * "Ground Transportation", "Rental Cars", or "Catering Coordination" that
 * duplicate a catalog chip under a different spelling. Keys are `serviceKey`
 * normalized.
 */
const SERVICE_ALIASES: Record<string, string> = {
  hangarstorage: "Hangar",
  groundtransportation: "Ground Transport",
  cateringcoordination: "Catering",
  cateringservice: "Catering",
  customs: "Customs / FBO Handling",
  customsaoe: "Customs / FBO Handling",
  customsservice: "Customs / FBO Handling",
  customshandling: "Customs / FBO Handling",
  limousine: "Limousine / Car Service",
  limousineservice: "Limousine / Car Service",
  limoservice: "Limousine / Car Service",
  carservice: "Limousine / Car Service",
  lavservice: "Lavatory Service",
  deice: "De-icing",
  gpuservice: "GPU",
  aircraftwash: "Aircraft Cleaning",
};

const CATALOG_BY_KEY: Record<string, string> = Object.fromEntries(
  ALL_SERVICE_OPTIONS.map((s) => [serviceKey(s), s]),
);

/**
 * Map a free-text service name onto its canonical catalog spelling, so the
 * same service never shows up twice under two spellings ("Hangar" vs
 * "Hangar Storage", "Rental Car" vs "Rental Cars"). Unknown names are
 * returned trimmed but otherwise untouched.
 */
export function canonicalServiceName(name: string): string {
  const trimmed = name.trim();
  const key = serviceKey(trimmed);
  const singular = key.endsWith("s") ? key.slice(0, -1) : null;
  return (
    CATALOG_BY_KEY[key] ??
    SERVICE_ALIASES[key] ??
    (singular ? (CATALOG_BY_KEY[singular] ?? SERVICE_ALIASES[singular]) : undefined) ??
    trimmed
  );
}

/**
 * Does this service name order fuel? Matches the catalog's explicit fuel
 * products AND legacy free-text names ("Fuel", "Jet A", "Avgas 100LL") so
 * requests created before the catalog unification keep working.
 */
export function isFuelServiceName(name: string): boolean {
  return /fuel|avgas|100\s*ll|\bjet\s*-?\s*a\b|saf\s*blend/i.test(name);
}

/** Jet-A fuel (the only product with the Prist additive choice). */
export function isJetAServiceName(name: string): boolean {
  return /\bjet\s*-?\s*a\b|jet-a/i.test(name) && !/saf/i.test(name);
}

/**
 * Concierge / arranged-for-the-passenger services. These get passenger and
 * provider tracking on the handheld (who the rental car is in the name of,
 * which limousine company, contact info).
 */
export function isConciergeServiceName(name: string): boolean {
  return /rental|limo|transport|car\s*service|shuttle|taxi|catering|customs|hotel|concierge/i.test(
    name,
  );
}

/**
 * Resolve the stored fuel type from a selected fuel service.
 * Jet-A honors the Prist additive choice; other products map directly.
 * Returns null for non-fuel services.
 */
export function fuelTypeForServiceName(
  name: string,
  pristAdditive: boolean,
): string | null {
  if (!isFuelServiceName(name)) return null;
  if (/saf/i.test(name)) return "SAF Blend";
  if (/avgas|100\s*ll/i.test(name)) return "Avgas 100LL";
  if (isJetAServiceName(name) || /^fuel$/i.test(name.trim())) {
    return pristAdditive ? "Jet A With Prist" : "Jet A";
  }
  return null;
}
